import { useEffect, useRef, useState } from 'react';

import useGoogleGeolocationLibrary from 'hooks/geolocation/use-google-geolocation-library';
import { useLocale } from 'state/intl';
import { REGIONS } from 'state/intl/types';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';

const LOCATION_LENGTH_THRESHOLD_FOR_PREDICTIONS: number = 3;

function useLocationPredictions(
  location: string,
  {
    includeExactMatch = false,
    matchTypes = [],
  }: {
    includeExactMatch?: boolean;
    matchTypes?: ('address' | '(region)' | 'geocode' | 'establishment' | '(cities)')[];
  } = {}
) {
  const { libraryLoaded } = useGoogleGeolocationLibrary();
  const { region } = useLocale();
  const [predictions, setPredictions] = useState<google.maps.places.AutocompletePrediction[]>([]);
  const autocompleteServiceRef = useRef<google.maps.places.AutocompleteService>();

  const countryStoreRestrictionFlag =
    useFlag<keyof typeof REGIONS | 'auto'>(
      LaunchDarklyFlag.ENABLE_OVERRIDE_COUNTRY_STORE_RESTRICTION
    ) || 'auto';
  const countryStoreRestriction =
    countryStoreRestrictionFlag === 'auto' ? region : countryStoreRestrictionFlag;

  if (libraryLoaded && !autocompleteServiceRef.current) {
    autocompleteServiceRef.current = new window.google.maps.places.AutocompleteService();
  }

  useEffect(() => {
    if (!libraryLoaded) {
      return;
    }

    if (!location || location.length < LOCATION_LENGTH_THRESHOLD_FOR_PREDICTIONS) {
      setPredictions(() => []);
      return;
    }

    const request = {
      input: location,
      types: matchTypes,
      componentRestrictions: { country: countryStoreRestriction },
    };

    autocompleteServiceRef?.current?.getPlacePredictions(
      request,
      (results, status: google.maps.places.PlacesServiceStatus) => {
        const hasResults =
          status === window.google.maps.places.PlacesServiceStatus.OK && results && results.length;
        setPredictions(hasResults ? () => results : () => []);
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [libraryLoaded, location]);

  if (!includeExactMatch) {
    return predictions.filter(prediction => prediction.description !== location);
  }

  return predictions;
}

export default useLocationPredictions;
